/**
 * DashboardPayantController — page /tableau-de-bord.html (§15.B, EPIC-032.4).
 *
 * Cloné dans la page par `dashboard-router` quand `org.status === "active"`.
 * Hydrate l'ensemble du dashboard depuis `GET /dashboard/me`, qui agrège
 * tous les modules (kpis, seuils, urssaf, tva,
 * aides, alertes, activité, échéances, suivi INPI).
 *
 * Pivots :
 *   - `data.mode === "active"`         → tout sauf inpiSection
 *   - `data.mode === "awaiting_siret"` → uniquement inpi + aides + activité
 *                                        + alertes profil (cat. 2 et 3)
 *
 * Conventions :
 *   - Pas de HTML inline : <template id="tpl-dashboard-…"> + cloneTemplate +
 *     fillSlots. Les attributs `data-slot-attr-{class,style}` sont des markers
 *     que le controller exploite manuellement (utilitaire `template.js` ne les
 *     traite pas).
 *   - Sections financières masquées en awaiting_siret via `hidden`, le DOM
 *     reste intact (cf. note d'implémentation du sous-EPIC).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { buildEventMapping } from "/js/utils/activity_feed_mapping.js";
import { isChargePayable, mountObligationPaymentForm } from "/js/utils/obligation_payment_form.js";

const ALERT_LEVEL_CLASS = {
  critical: "nm-banner--critical",
  warning: "nm-banner--warning",
  info: "nm-banner--info",
};

const ALERT_CATEGORIES_PROFILE = new Set([
  "aids_acre_ending",
  "aids_acre_renewal",
  "aids_arce_upcoming",
  "aids_are_status",
]);

const PILL_CLASSES = [
  "nm-pill--blue",
  "nm-pill--ochre",
  "nm-pill--olive",
  "nm-pill--rust",
  "nm-pill--indigo",
  "nm-pill--red",
  "nm-pill--accent",
];

const DOMAIN_PILL = {
  urssaf: "nm-pill--blue",
  tva: "nm-pill--ochre",
  acre: "nm-pill--olive",
  cfe: "nm-pill--ochre",
  ir: "nm-pill--indigo",
  assurance: "nm-pill--blue",
  abonnement: "nm-pill--indigo",
  livre_recettes: "nm-pill--accent",
  inpi: "nm-pill--blue",
  das2: "nm-pill--ochre",
};

const THRESHOLD_LEVEL_CLASS = {
  warning: "is-warning",
  error: "is-error",
};

const FRENCH_NUMBER = new Intl.NumberFormat("fr-FR");
const FRENCH_PERCENT = new Intl.NumberFormat("fr-FR", {
  style: "percent",
  maximumFractionDigits: 1,
});

export default class DashboardPayantController extends BaseController {
  static targets = [
    // En-tête période (titre + sous-titre régime). EPIC-099.6.
    "periodLabel",
    "periodRegime",
    // KPIs CA / charges cumulées.
    "kpiRevenueYtdLabel",
    "kpiRevenueYtd",
    "kpiRevenueDelta",
    "kpiChargesLabel",
    "kpiCumulativeCharges",
    // KPIs « À encaisser » / « En retard » EPIC-099.6.
    "kpiReceivableAmount",
    "kpiReceivableDesc",
    "kpiOverdueAmount",
    "kpiOverdueDesc",
    // Bloc TVA.
    "vatSection",
    "kpiVatCollected",
    "kpiVatCollectedSub",
    "kpiVatDeductible",
    "kpiVatDeductibleSub",
    "kpiVatNet",
    "kpiVatNextDeclarationSub",
    // Alertes.
    "alertsSection",
    "alertsList",
    // Encart INPI awaiting_siret.
    "inpiSection",
    "inpiStatus",
    "inpiIntroParagraph",
    "inpiRegularizationsList",
    // Ritual livre des recettes (§7.H.E / EPIC-099 / 99.10).
    "ritualSection",
    "ritualMonthLabel",
    "ritualHelpText",
    "ritualViewLink",
    "ritualConfirmButton",
    // Seuils.
    "thresholdsSection",
    // Aides ACRE / ARE / ARCE.
    "aidsSection",
    "aidAcre",
    "aidAcreLabel",
    "aidAre",
    "aidAreLabel",
    "aidArce",
    "aidArceLabel",
    // EPIC-032 / 32.5 — saisie inline 2ᵉ tranche ARCE (fenêtre J-7).
    "aidArceForm",
    // Activité récente.
    "activityFeedSection",
    "activityFeedList",
    // À venir.
    "upcomingSection",
    "upcomingList",
    // EPIC-043 / 43.5 — encart sessions caisse ouvertes (§15.B).
    "cashSessionAlerts",
    // EPIC-046.6 — encart import historique (§15.B), dismissable via localStorage.
    "importBanner",
    "importBannerCutover",
  ];

  static IMPORT_BANNER_STORAGE_KEY = "nm_pref_dashboard_import_banner_dismissed_at";

  async connect() {
    this._loadImportBanner();
    let response;
    try {
      response = await api.dashboard.getMe();
    } catch (_err) {
      // 401 → /login déjà géré par api.js. Autres erreurs : on garde le
      // skeleton visible (graceful degradation), la page reste lisible.
      return;
    }
    const data = response;
    if (!data) return;

    const mode = data.mode === "awaiting_siret" ? "awaiting_siret" : "active";
    this._applyMode(mode);

    if (mode === "active") {
      this._renderPeriod(data.period || null);
      this._renderKpisRevenue(data.kpis || {});
      this._renderKpiCharges(data.kpis || {});
      this._renderKpiReceivable(data.kpis || {});
      this._renderKpiOverdue(data.kpis || {});
      this._renderKpisVat(data.kpis || {});
      this._renderThresholds(data.thresholds || []);
      this._renderUpcoming(data.upcomingObligations);
      this._renderRitual(data.ritual || { visible: false });
    }

    this._renderAids(data.aids || {});
    this._renderAlerts(data.alerts || [], mode);
    this._renderActivityFeed(data.activityFeed);
    this._renderInpiFollowup(data.inpiFollowup || null);

    if (mode === "active") {
      // EPIC-043 / 43.5 — second fetch non bloquant des sessions caisse
      // ouvertes (§15.B). On évite d'attendre la fin pour ne pas retarder
      // l'affichage du dashboard ; l'encart se peuple dès que la réponse
      // arrive. Erreurs silencieuses (la zone reste cachée).
      this._fetchOpenCashSessions();
    }
  }

  // ---------------------------------------------------------------------------
  // Encart sessions caisse ouvertes (§15.B / EPIC-043 / 43.5)
  // ---------------------------------------------------------------------------

  async _fetchOpenCashSessions() {
    if (!this.hasCashSessionAlertsTarget) return;
    // EPIC-099.20 — source unique d'oldestOpenedAt + openSessionsCount via
    // l'endpoint dédié. Si le tenant n'a aucune session ouverte, on évite
    // le second appel `listSessions`.
    let status;
    try {
      status = await api.cashRegister.status();
    } catch (_err) {
      return;
    }
    const count = Number(status?.openSessionsCount) || 0;
    if (count === 0) {
      this.cashSessionAlertsTarget.hidden = true;
      return;
    }
    let response;
    try {
      response = await api.cashRegister.listSessions({ status: "open", perPage: 5 });
    } catch (_err) {
      return;
    }
    const sessions = Array.isArray(response?.data) ? response.data : [];
    this._renderOpenCashSessions(sessions);
  }

  _renderOpenCashSessions(sessions) {
    if (!this.hasCashSessionAlertsTarget) return;
    const container = this.cashSessionAlertsTarget;
    while (container.firstChild) container.removeChild(container.firstChild);
    if (sessions.length === 0) {
      container.hidden = true;
      return;
    }
    for (const session of sessions) {
      const fragment = this.cloneTemplate("cash-session-alert-template");
      if (!fragment) continue;
      const tone = this._computeCashSessionTone(session.openedAt);
      const dateLabel = this._formatCashSessionDate(session.sessionDate);
      const sessionRef = session.label ? `« ${session.label} »` : `du ${dateLabel}`;
      let label;
      if (tone.severity === "info") {
        label = `Session caisse ${sessionRef} en cours.`;
      } else if (tone.severity === "critical") {
        label = `Session caisse ${sessionRef} ouverte depuis ${tone.daysOpen} jours — pensez à la fermer.`;
      } else {
        label = `Session caisse ${sessionRef} à fermer.`;
      }
      const banner = fragment.querySelector(".nm-banner");
      if (banner) banner.classList.add(tone.bannerClass);
      this.fillSlots(fragment, {
        "session-label": label,
        "opened-at": session.openedAt
          ? ` Ouverte le ${this._formatCashSessionDateTime(session.openedAt)}.`
          : "",
      });
      const link = fragment.querySelector('[data-slot="session-link"]');
      if (link) {
        link.setAttribute("href", `/caisse-session.html?id=${encodeURIComponent(session.id)}`);
        link.textContent = "Voir la session →";
      }
      const closeLink = fragment.querySelector('[data-slot="session-close-link"]');
      if (closeLink) {
        closeLink.setAttribute(
          "href",
          `/caisse-session.html?id=${encodeURIComponent(session.id)}#close-section`,
        );
        closeLink.textContent = "Fermer la session";
      }
      container.appendChild(fragment);
    }
    container.hidden = false;
  }

  // EPIC-099.20 — paliers UI §7.J.H : J neutre (info), J+1 à J+6
  // warning, J+7+ critical (cohérent avec l'email J+7 §19.A). Pas de
  // palier intermédiaire J+3 côté UI (J+3 reste un palier email).
  _computeCashSessionTone(openedAtIso) {
    if (!openedAtIso) {
      return { severity: "warning", bannerClass: "nm-banner--warning", daysOpen: 0 };
    }
    const openedAt = new Date(openedAtIso);
    if (Number.isNaN(openedAt.getTime())) {
      return { severity: "warning", bannerClass: "nm-banner--warning", daysOpen: 0 };
    }
    const startOfOpenedDay = new Date(
      openedAt.getFullYear(),
      openedAt.getMonth(),
      openedAt.getDate(),
    );
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysOpen = Math.max(
      0,
      Math.round((startOfToday.getTime() - startOfOpenedDay.getTime()) / 86400000),
    );
    if (daysOpen >= 7) {
      return { severity: "critical", bannerClass: "nm-banner--critical", daysOpen };
    }
    if (daysOpen >= 1) {
      return { severity: "warning", bannerClass: "nm-banner--warning", daysOpen };
    }
    return { severity: "info", bannerClass: "nm-banner--info", daysOpen };
  }

  _formatCashSessionDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  }

  _formatCashSessionDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // ---------------------------------------------------------------------------
  // Mode (active vs awaiting_siret)
  // ---------------------------------------------------------------------------

  _applyMode(mode) {
    if (mode === "awaiting_siret") {
      this._hideFinancialBlocks();
      if (this.hasInpiSectionTarget) this.inpiSectionTarget.hidden = false;
      return;
    }
    if (this.hasInpiSectionTarget) this.inpiSectionTarget.hidden = true;
  }

  _hideFinancialBlocks() {
    const targetsToHide = [
      "vatSectionTarget",
      "ritualSectionTarget",
      "thresholdsSectionTarget",
      "upcomingSectionTarget",
      "cashSessionAlertsTarget",
    ];
    for (const name of targetsToHide) {
      const hasKey = `has${name.charAt(0).toUpperCase()}${name.slice(1)}`;
      if (this[hasKey]) this[name].hidden = true;
    }
    // Sections sans target dédié à masquer aussi : `nm-stats` (KPIs CA/URSSAF)
    // et toutes les `.nm-card` financières du `.nm-grid-2` qui suit (Dernières
    // factures, Évolution CA mensuel, Conseil) — repérables par leur absence
    // de `data-dashboard-payant-target`.
    const stats = this.element.querySelector(":scope > .nm-stats");
    if (stats) stats.hidden = true;
    const firstGrid = this.element.querySelector(":scope > .nm-grid-2");
    if (firstGrid) firstGrid.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // En-tête période (titre + sous-titre régime) EPIC-099.6
  // ---------------------------------------------------------------------------

  _renderPeriod(period) {
    if (!period) return;
    if (this.hasPeriodLabelTarget && typeof period.label === "string") {
      this.periodLabelTarget.textContent = period.label;
    }
    if (this.hasPeriodRegimeTarget && typeof period.regimeDescription === "string") {
      this.periodRegimeTarget.textContent = period.regimeDescription;
    }
    const year = Number(period.year);
    if (Number.isFinite(year) && year > 0) {
      if (this.hasKpiRevenueYtdLabelTarget) {
        this.kpiRevenueYtdLabelTarget.textContent = `CA cumulé ${year}`;
      }
      if (this.hasKpiChargesLabelTarget) {
        this.kpiChargesLabelTarget.textContent = `Charges cumulées ${year}`;
      }
      if (this.hasKpiVatCollectedSubTarget) {
        this.kpiVatCollectedSubTarget.textContent = `Sur factures émises ${year}`;
      }
      if (this.hasKpiVatDeductibleSubTarget) {
        this.kpiVatDeductibleSubTarget.textContent = `Sur achats ${year}`;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // KPIs CA / URSSAF / TVA
  // ---------------------------------------------------------------------------

  _renderKpisRevenue(kpis) {
    if (this.hasKpiRevenueYtdTarget) {
      this.kpiRevenueYtdTarget.innerHTML = this._formatEuroHtml(kpis.revenueYtd);
    }
    if (this.hasKpiRevenueDeltaTarget) {
      const ytd = Number(kpis.revenueYtd ?? 0);
      const prev = Number(kpis.revenueYtdPreviousYear ?? 0);
      if (prev > 0) {
        const ratio = (ytd - prev) / prev;
        const sign = ratio >= 0 ? "+" : "−";
        const pct = FRENCH_PERCENT.format(Math.abs(ratio));
        this.kpiRevenueDeltaTarget.textContent = `${sign} ${pct} vs ${this._previousYear()} (${this._formatEuro(prev)})`;
        this._applyDeltaClass(this.kpiRevenueDeltaTarget, ratio);
      } else {
        this.kpiRevenueDeltaTarget.textContent = "Pas de comparable l'an dernier";
        this._applyDeltaClass(this.kpiRevenueDeltaTarget, 0);
      }
    }
  }

  _renderKpiReceivable(kpis) {
    const receivable = kpis.receivable ?? null;
    const amount = Number(receivable?.amount ?? 0);
    const count = Number(receivable?.invoiceCount ?? 0);
    if (this.hasKpiReceivableAmountTarget) {
      this.kpiReceivableAmountTarget.innerHTML = this._formatEuroHtml(amount);
    }
    if (this.hasKpiReceivableDescTarget) {
      const noun = count > 1 ? "factures" : "facture";
      this.kpiReceivableDescTarget.textContent = count > 0
        ? `${count} ${noun} en attente`
        : "Aucune facture en attente";
      this.kpiReceivableDescTarget.classList.remove("is-warning");
      if (count > 0) this.kpiReceivableDescTarget.classList.add("is-warning");
    }
  }

  _renderKpiOverdue(kpis) {
    const overdue = kpis.overdue ?? null;
    const amount = Number(overdue?.amount ?? 0);
    const count = Number(overdue?.invoiceCount ?? 0);
    const oldestDays = Number(overdue?.oldestDays ?? 0);
    if (this.hasKpiOverdueAmountTarget) {
      this.kpiOverdueAmountTarget.innerHTML = this._formatEuroHtml(amount);
    }
    if (this.hasKpiOverdueDescTarget) {
      if (count > 0) {
        const noun = count > 1 ? "factures" : "facture";
        this.kpiOverdueDescTarget.textContent = oldestDays > 0
          ? `${count} ${noun} · ${oldestDays} jours`
          : `${count} ${noun}`;
      } else {
        this.kpiOverdueDescTarget.textContent = "Aucune facture en retard";
      }
      this.kpiOverdueDescTarget.classList.remove("is-error");
      if (count > 0) this.kpiOverdueDescTarget.classList.add("is-error");
    }
  }

  _applyDeltaClass(el, ratio) {
    el.classList.remove("is-success", "is-warning", "is-error");
    if (ratio > 0) el.classList.add("is-success");
    else if (ratio < 0) el.classList.add("is-error");
  }

  _previousYear() {
    return String(new Date().getFullYear() - 1);
  }

  _renderKpiCharges(kpis) {
    if (this.hasKpiCumulativeChargesTarget) {
      this.kpiCumulativeChargesTarget.innerHTML = this._formatEuroHtml(kpis.cumulativeCharges);
    }
  }

  _renderKpisVat(kpis) {
    if (!this.hasVatSectionTarget) return;
    const liable = kpis.vatLiable === true;
    this.vatSectionTarget.hidden = !liable;
    if (!liable) return;
    if (this.hasKpiVatCollectedTarget) {
      this.kpiVatCollectedTarget.innerHTML = this._formatEuroHtml(kpis.vatCollected);
    }
    if (this.hasKpiVatDeductibleTarget) {
      this.kpiVatDeductibleTarget.innerHTML = this._formatEuroHtml(kpis.vatDeductible);
    }
    if (this.hasKpiVatNetTarget) {
      this.kpiVatNetTarget.innerHTML = this._formatEuroHtml(kpis.vatNet);
    }
    if (this.hasKpiVatNextDeclarationSubTarget) {
      const next = kpis.vatNextDeclaration ?? null;
      this.kpiVatNextDeclarationSubTarget.textContent = next?.shortLabel
        ? next.shortLabel
        : "Prochaine échéance à venir";
    }
  }

  // ---------------------------------------------------------------------------
  // Seuils
  // ---------------------------------------------------------------------------

  _renderThresholds(thresholds) {
    if (!this.hasThresholdsSectionTarget) return;
    // Le bloc reste visible avec son titre statique. On remplace les jauges
    // existantes (les 2 jauges de la maquette servent de skeleton) par les
    // jauges réelles via le template `tpl-dashboard-threshold`.
    const existing = this.thresholdsSectionTarget.querySelectorAll(".nm-jauge");
    existing.forEach((node) => node.remove());

    for (const threshold of thresholds) {
      const fragment = this.cloneTemplate("tpl-dashboard-threshold");
      this.fillSlots(fragment, {
        label: this._thresholdLabel(threshold.type),
        amount: `${this._formatEuro(threshold.currentValue)} / ${this._formatEuro(threshold.limitValue)}`,
        foot: `${this._formatPercent(threshold.ratio)} atteint`,
      });
      const fill = fragment.querySelector("[data-slot-attr-style='fillStyle']");
      if (fill) {
        const widthPct = Math.min(100, Math.max(0, Number(threshold.ratio ?? 0) * 100));
        fill.setAttribute("style", `width: ${widthPct}%;`);
        const levelClass = THRESHOLD_LEVEL_CLASS[threshold.level];
        if (levelClass) fill.classList.add(levelClass);
        fill.removeAttribute("data-slot-attr-style");
      }
      this.thresholdsSectionTarget.appendChild(fragment);
    }
  }

  // Le backend (DashboardPayantService::buildThresholds) émet des types
  // `vat_basic_<catégorie>` (un par catégorie active) et `micro_mixed`.
  // On les traduit en libellés humains, alignés sur la page Seuils
  // (`seuils-dashboard` : « Franchise en base de TVA », « BIC vente »…).
  _thresholdLabel(type) {
    if (type === "micro_mixed") {
      return "Plafond régime micro (activité mixte)";
    }
    if (typeof type === "string" && type.startsWith("vat_basic_")) {
      return `Franchise TVA (${this._categoryLabel(type.slice("vat_basic_".length))})`;
    }
    return type || "—";
  }

  _categoryLabel(category) {
    const labels = {
      bic_vente: "BIC vente",
      bic_services: "BIC services",
      bnc: "BNC",
    };
    return labels[category] || category || "—";
  }

  // ---------------------------------------------------------------------------
  // Aides ACRE / ARE / ARCE
  // ---------------------------------------------------------------------------

  _renderAids(aids) {
    const hasAcre = aids.acre !== null && aids.acre !== undefined && Boolean(aids.acre.expiresOn);
    // EPIC-099.7 — ligne ARE masquée si remainingMonths est null (reliquat
    // glissant ≤ 0 ou champs source manquants), décision 2026-05-26.
    const hasAre = aids.are !== null && aids.are !== undefined
      && Number.isFinite(Number(aids.are.remainingMonths));
    const hasArce = aids.arce !== null && aids.arce !== undefined
      && (aids.arce.secondPaymentReceivedDate || aids.arce.secondPaymentDueDate);

    if (this.hasAidAcreTarget) this.aidAcreTarget.hidden = !hasAcre;
    if (this.hasAidAreTarget) this.aidAreTarget.hidden = !hasAre;
    if (this.hasAidArceTarget) this.aidArceTarget.hidden = !hasArce;

    if (hasAcre && this.hasAidAcreLabelTarget) {
      this.aidAcreLabelTarget.replaceChildren(
        document.createTextNode("Exonération partielle jusqu'au "),
        this._strong(this.formatDate(aids.acre.expiresOn)),
      );
    }

    if (hasAre && this.hasAidAreLabelTarget) {
      const months = Number(aids.are.remainingMonths);
      const suffix = months > 1 ? "s" : "";
      this.aidAreLabelTarget.replaceChildren(
        document.createTextNode("Solde estimé : "),
        this._strong(`${months} mois restant${suffix}`),
      );
    }

    if (hasArce && this.hasAidArceLabelTarget) {
      const arce = aids.arce;
      if (arce.secondPaymentReceivedDate) {
        this.aidArceLabelTarget.replaceChildren(
          document.createTextNode("Reçu le "),
          this._strong(this.formatDate(arce.secondPaymentReceivedDate)),
        );
      } else {
        this.aidArceLabelTarget.replaceChildren(
          document.createTextNode("Prévu : "),
          this._strong(this.formatMonthYear(arce.secondPaymentDueDate)),
        );
      }
    }

    // EPIC-032 / 32.5 — fenêtre de saisie inline 2ᵉ tranche ARCE.
    // Visible si today ≥ dueDate − 7 jours ET pas encore réceptionnée.
    if (this.hasAidArceFormTarget) {
      const arce = hasArce ? aids.arce : null;
      this.aidArceFormTarget.hidden = !this._isArceInlineFormVisible(arce);
    }

    if (this.hasAidsSectionTarget) {
      this.aidsSectionTarget.hidden = !(hasAcre || hasAre || hasArce);
    }
  }

  _strong(text) {
    const el = document.createElement("strong");
    el.textContent = text;
    return el;
  }

  _isArceInlineFormVisible(arce) {
    if (!arce) return false;
    if (arce.secondPaymentReceivedDate) return false;
    const dueDate = arce.secondPaymentDueDate;
    if (!dueDate) return false;
    const due = new Date(dueDate);
    if (Number.isNaN(due.getTime())) return false;
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() >= due.getTime() - sevenDaysMs;
  }

  /**
   * Refetch /dashboard/me et re-rend l'ensemble. Appelé après une
   * action inline (CFE marquée payée, 2ᵉ tranche ARCE réceptionnée).
   */
  async refresh() {
    return this.connect();
  }

  // ---------------------------------------------------------------------------
  // Alertes
  // ---------------------------------------------------------------------------

  _renderAlerts(alerts, mode) {
    if (!this.hasAlertsListTarget) return;
    this.alertsListTarget.replaceChildren();

    const filtered = mode === "awaiting_siret"
      ? alerts.filter((a) => ALERT_CATEGORIES_PROFILE.has(a.category))
      : alerts;

    if (filtered.length === 0) {
      if (this.hasAlertsSectionTarget) this.alertsSectionTarget.hidden = true;
      return;
    }
    if (this.hasAlertsSectionTarget) this.alertsSectionTarget.hidden = false;

    for (const alert of filtered) {
      const fragment = this.cloneTemplate("tpl-dashboard-alert");
      this.fillSlots(fragment, {
        title: alert.title || "",
        message: alert.message ? ` ${alert.message}` : "",
      });
      const banner = fragment.querySelector(".nm-banner");
      if (banner) {
        const levelClass = ALERT_LEVEL_CLASS[alert.level] || ALERT_LEVEL_CLASS.info;
        banner.classList.add(levelClass);
        banner.removeAttribute("data-slot-attr-class");
      }
      const ctaWrap = fragment.querySelector("[data-slot='ctasHtml']");
      if (ctaWrap) {
        ctaWrap.removeAttribute("data-slot-attr-style");
        const ctas = Array.isArray(alert.ctas) ? alert.ctas : [];
        if (ctas.length === 0) {
          ctaWrap.remove();
        } else {
          ctaWrap.setAttribute("style", "margin-top: 10px;");
          ctaWrap.replaceChildren();
          for (const cta of ctas) {
            ctaWrap.appendChild(this._buildAlertCta(cta));
          }
        }
      }
      this.alertsListTarget.appendChild(fragment);
    }
  }

  /**
   * Construit un CTA d'alerte. EPIC-032 / 32.5 : reconnaît
   * `cta.kind === 'cfe_mark_paid'` (catégorie 4) et produit un
   * `<button>` qui ouvre le dialog du controller `dashboard-cfe-paid`.
   * Les autres CTAs restent des `<a href>` standards.
   */
  _buildAlertCta(cta) {
    if (cta.kind === "cfe_mark_paid") {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = cta.label || "J'ai payé";
      btn.className = "nm-btn nm-btn--solid nm-btn--sm";
      btn.setAttribute("data-action", "click->dashboard-cfe-paid#open");
      if (cta.year !== undefined && cta.year !== null) {
        btn.setAttribute("data-year", String(cta.year));
      }
      return btn;
    }
    const a = document.createElement("a");
    a.href = cta.href || "#";
    a.textContent = cta.label || "";
    a.className = cta.primary
      ? "nm-btn nm-btn--solid nm-btn--sm"
      : "nm-btn nm-btn--link";
    return a;
  }

  // ---------------------------------------------------------------------------
  // Activité récente (placeholder si null)
  // ---------------------------------------------------------------------------

  _renderActivityFeed(feed) {
    if (!this.hasActivityFeedListTarget) return;
    this.activityFeedListTarget.replaceChildren();

    if (feed === null || feed === undefined) {
      this._appendPlaceholderLi(this.activityFeedListTarget, "Module en cours d'activation.");
      return;
    }
    const events = Array.isArray(feed.events) ? feed.events : [];
    if (events.length === 0) {
      this._appendPlaceholderLi(this.activityFeedListTarget, "Aucune activité récente.");
      return;
    }
    for (const event of events.slice(0, 10)) {
      // Mapping partagé eventType + metadata → libellé humain + domaine
      // (même source de vérité que la page /activite, cf. EPIC-029).
      // Le backend ne renvoie que `eventType`/`domain`/`metadata` ; sans
      // ce mapping le slot affichait le code brut de l'enum.
      const mapping = buildEventMapping(event);
      const fragment = this.cloneTemplate("tpl-dashboard-activity");
      this.fillSlots(fragment, {
        when: this.formatTimeAgo(event.occurredAt),
        what: mapping.label,
        who: mapping.domainLabel,
      });
      this.activityFeedListTarget.appendChild(fragment);
    }
  }

  // ---------------------------------------------------------------------------
  // À venir (placeholder si null)
  // ---------------------------------------------------------------------------

  _renderUpcoming(upcoming) {
    if (!this.hasUpcomingListTarget) return;
    this.upcomingListTarget.replaceChildren();

    if (upcoming === null || upcoming === undefined) {
      this._appendPlaceholderLi(this.upcomingListTarget, "Module en cours d'activation.");
      return;
    }
    const items = Array.isArray(upcoming.items) ? upcoming.items : [];
    if (items.length === 0) {
      this._appendPlaceholderLi(this.upcomingListTarget, "Aucune échéance dans les 12 prochains mois.");
      return;
    }
    for (const item of items.slice(0, 10)) {
      const fragment = this.cloneTemplate("tpl-dashboard-upcoming");
      this.fillSlots(fragment, {
        when: item.dateRelative || this.formatRelativeDate(item.dueDate),
        pillLabel: item.domainLabel || item.domain || "",
        label: item.label || "",
      });
      const pill = fragment.querySelector("[data-slot='pillLabel']");
      if (pill) {
        pill.removeAttribute("data-slot-attr-class");
        PILL_CLASSES.forEach((c) => pill.classList.remove(c));
        const pillClass = DOMAIN_PILL[item.domain] || "nm-pill--blue";
        pill.classList.add(pillClass);
      }

      // EPIC-066 / 66.2 — saisie du montant payé sur les charges
      // (URSSAF, TVA, CFE, IR) portant une `periodKey`.
      const markPaidBtn = fragment.querySelector("[data-slot='markPaidBtn']");
      if (markPaidBtn) {
        if (isChargePayable(item)) {
          markPaidBtn.hidden = false;
          markPaidBtn.setAttribute("aria-expanded", "false");
          markPaidBtn.addEventListener("click", (event) => this._toggleObligationPaymentForm(event, item));
        } else {
          markPaidBtn.remove();
        }
      }

      this.upcomingListTarget.appendChild(fragment);
    }
  }

  /**
   * Déplie/replie le form inline « Marquer comme payé » dans l'item
   * `<li>` du bloc « À venir » (EPIC-066 / 66.2). Le form est inséré
   * comme bloc enfant du `<li>` (pas un `<li>` frère, pour ne pas créer
   * de puce de timeline parasite).
   */
  _toggleObligationPaymentForm(event, item) {
    const button = event.currentTarget;
    const li = button.closest("li");
    if (!li) return;

    const existing = li.querySelector(".nm-obligation-pay-row");
    if (existing) {
      existing.remove();
      button.setAttribute("aria-expanded", "false");

      return;
    }

    const fragment = this.cloneTemplate("tpl-obligation-payment-form");
    const formRoot = fragment.querySelector(".nm-obligation-pay-row");
    mountObligationPaymentForm(fragment, {
      obligationKind: item.domain,
      periodKey: item.periodKey,
      onSuccess: () => {
        formRoot?.remove();
        button.disabled = true;
        button.textContent = "Payé ✓";
        button.setAttribute("aria-expanded", "false");
      },
      onCancel: () => {
        formRoot?.remove();
        button.setAttribute("aria-expanded", "false");
        button.focus();
      },
    });
    li.appendChild(formRoot);
    button.setAttribute("aria-expanded", "true");
    formRoot?.querySelector("[data-slot='amount']")?.focus();
  }

  _appendPlaceholderLi(list, text) {
    const li = document.createElement("li");
    li.className = "text-muted";
    li.textContent = text;
    list.appendChild(li);
  }

  // ---------------------------------------------------------------------------
  // Rituel mensuel — livre des recettes §7.H.E (EPIC-099 / 99.10)
  // ---------------------------------------------------------------------------

  _renderRitual(ritual) {
    if (!this.hasRitualSectionTarget) return;
    if (!ritual || ritual.visible !== true) {
      this.ritualSectionTarget.hidden = true;
      return;
    }

    const year = Number(ritual.year);
    const month = Number(ritual.month);
    const entriesCount = Number(ritual.entriesCount ?? 0);
    const totalCents = Number(ritual.totalAmountInclVatCents ?? 0);
    const totalLabel = this._formatEuroFromCents(totalCents);
    const entriesLabel = entriesCount > 1 ? `${entriesCount} entrées` : `${entriesCount} entrée`;
    const monthLabel = String(ritual.monthLabel || "");

    if (this.hasRitualMonthLabelTarget) {
      this.ritualMonthLabelTarget.textContent = `Recettes de ${monthLabel.toLowerCase()} — ${entriesLabel}, ${totalLabel}`;
    }
    if (this.hasRitualViewLinkTarget && Number.isFinite(year) && Number.isFinite(month)) {
      // EPIC-099 / 99.27 — l'index annuel ne porte plus le détail mois ;
      // le rituel du dashboard pointe désormais vers la page mois détail.
      this.ritualViewLinkTarget.setAttribute(
        "href",
        `/livre-des-recettes-detail.html?year=${year}&month=${String(month).padStart(2, "0")}`,
      );
    }
    if (this.hasRitualConfirmButtonTarget) {
      const confirmable = ritual.confirmable !== false;
      this.ritualConfirmButtonTarget.disabled = !confirmable;
      this.ritualConfirmButtonTarget.dataset.year = String(year);
      this.ritualConfirmButtonTarget.dataset.month = String(month);
    }

    this.ritualSectionTarget.hidden = false;
  }

  async openRitualConfirmModal(event) {
    event.preventDefault();
    if (!this.hasRitualConfirmButtonTarget) return;
    const trigger = event.currentTarget;
    const year = Number.parseInt(trigger.dataset.year || "", 10);
    const month = Number.parseInt(trigger.dataset.month || "", 10);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;

    // EPIC-099 / 99.27 — la modale de confirmation vit désormais sur la
    // page mois détail. Le clic depuis le dashboard redirige vers cette
    // page avec `?confirm=1`, qui auto-déploie la modale au mount du
    // detail controller (deep-link partageable).
    window.location.href = `/livre-des-recettes-detail.html?year=${year}&month=${String(month).padStart(2, "0")}&confirm=1`;
  }

  closeRitualConfirmModal() {
    if (!this._ritualDialog) return;
    if (typeof this._ritualDialog.close === "function") this._ritualDialog.close();
    this._ritualDialog.remove();
    this._ritualDialog = null;
  }

  async submitRitualConfirmation() {
    if (!this._ritualDialog) return;
    const year = Number.parseInt(this._ritualDialog.dataset.year || "", 10);
    const month = Number.parseInt(this._ritualDialog.dataset.month || "", 10);
    const confirmBtn = this._ritualDialog.querySelector("[data-action*='submitRitualConfirmation']");
    const cancelBtn = this._ritualDialog.querySelector("[data-action*='closeRitualConfirmModal']");
    const errorEl = this._ritualDialog.querySelector(".nm-form__error");

    if (confirmBtn) confirmBtn.disabled = true;
    if (cancelBtn) cancelBtn.disabled = true;
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.hidden = true;
    }

    try {
      await api.confirmRevenueMonth(year, month);
      this.closeRitualConfirmModal();
      this.showSuccess("Mois confirmé au livre des recettes.");
      this.connect();
    } catch (error) {
      const status = error && typeof error === "object" ? error.status : null;
      if (status === 409) {
        this.closeRitualConfirmModal();
        this.showError("Ce mois a déjà été confirmé.");
        this.connect();
        return;
      }
      const message = error?.details?.fields?.[0]?.message
        || error?.message
        || "Erreur lors de la confirmation. Réessayez plus tard.";
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      if (confirmBtn) confirmBtn.disabled = false;
      if (cancelBtn) cancelBtn.disabled = false;
    }
  }

  _formatEuroFromCents(cents) {
    const euros = Number(cents ?? 0) / 100;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(euros);
  }

  // ---------------------------------------------------------------------------
  // Suivi INPI (mode awaiting_siret)
  // ---------------------------------------------------------------------------

  _renderInpiFollowup(followup) {
    if (!this.hasInpiSectionTarget) return;
    if (!followup) {
      // En mode active : la section est déjà hidden par _applyMode.
      return;
    }
    if (this.hasInpiStatusTarget) {
      this.inpiStatusTarget.replaceChildren();
      const dot = document.createElement("span");
      dot.className = "nm-pill__dot";
      this.inpiStatusTarget.appendChild(dot);
      this.inpiStatusTarget.appendChild(document.createTextNode(this._inpiStatusLabel(followup.currentStatus)));
    }
    if (this.hasInpiIntroParagraphTarget) {
      const deposited = followup.depositedAt ? this._formatLongDate(followup.depositedAt) : null;
      const delay = followup.expectedSiretDelayDays ?? null;
      const minDays = Number(delay?.min ?? 7);
      const maxDays = Number(delay?.max ?? 15);
      const sentence1 = deposited
        ? `Votre dossier de création a été déposé au Guichet Unique de l'INPI le ${deposited}.`
        : "Votre dossier de création a été déposé au Guichet Unique de l'INPI.";
      const sentence2 = `Vous recevrez votre SIRET sous ${minDays} à ${maxDays} jours ouvrés. En attendant, certaines fonctionnalités sont restreintes.`;
      this.inpiIntroParagraphTarget.textContent = `${sentence1} ${sentence2}`;
    }
    if (this.hasInpiRegularizationsListTarget) {
      this.inpiRegularizationsListTarget.replaceChildren();
      const reqs = Array.isArray(followup.regularizationRequests) ? followup.regularizationRequests : [];
      if (reqs.length === 0) {
        this._appendPlaceholderLi(this.inpiRegularizationsListTarget, "Aucune demande de régularisation en cours.");
        return;
      }
      for (const req of reqs) {
        const fragment = this.cloneTemplate("tpl-dashboard-regularization");
        this.fillSlots(fragment, {
          when: this.formatShortDate(req.requestedAt),
          statusLabel: this._inpiRegulLabel(req.status),
          title: req.reason || "—",
          who: req.dueDate ? `INPI · échéance ${this.formatShortDate(req.dueDate)}` : "INPI",
        });
        this.inpiRegularizationsListTarget.appendChild(fragment);
      }
    }
  }

  _inpiStatusLabel(status) {
    const labels = {
      pending: "en instruction",
      pending_regularization: "régularisation demandée",
      regularization_processing: "régularisation en cours",
      awaiting_deposit: "en attente de dépôt",
      accepted: "acceptée",
      rejected_definitive: "rejetée",
      in_review: "en cours",
    };
    return labels[status] || status || "—";
  }

  _inpiRegulLabel(status) {
    const labels = {
      pending: "à compléter",
      submitted: "transmise",
      processing: "en cours",
    };
    return labels[status] || status || "à compléter";
  }

  // ---------------------------------------------------------------------------
  // Helpers de formatage
  // ---------------------------------------------------------------------------

  _formatEuro(value) {
    const n = Number(value ?? 0);
    return `${FRENCH_NUMBER.format(Math.round(n))} €`;
  }

  _formatEuroHtml(value) {
    const n = Number(value ?? 0);
    return `${FRENCH_NUMBER.format(Math.round(n))} <small>€</small>`;
  }

  _formatPercent(ratio) {
    return FRENCH_PERCENT.format(Number(ratio ?? 0));
  }

  _formatLongDate(dateString) {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    } catch {
      return dateString;
    }
  }

  // ---------------------------------------------------------------------------
  // EPIC-046.6 — Encart import historique §15.B (dismissable via localStorage).
  // ---------------------------------------------------------------------------

  async _loadImportBanner() {
    if (!this.hasImportBannerTarget) return;
    try {
      if (window.localStorage.getItem(DashboardPayantController.IMPORT_BANNER_STORAGE_KEY)) return;
    } catch {
      // localStorage indisponible — on continue, l'encart sera ré-affiché à chaque visite.
    }
    let profile;
    try {
      profile = await api.getOrganizationProfile();
    } catch {
      return;
    }
    const cutover = profile?.importCutoverDate ?? null;
    if (!cutover) return;
    if (this.hasImportBannerCutoverTarget) {
      this.importBannerCutoverTarget.textContent = this.formatDate ? this.formatDate(cutover) : cutover;
      this.importBannerCutoverTarget.setAttribute("datetime", cutover);
    }
    this.importBannerTarget.hidden = false;
  }

  dismissImportBanner() {
    if (this.hasImportBannerTarget) this.importBannerTarget.hidden = true;
    try {
      window.localStorage.setItem(
        DashboardPayantController.IMPORT_BANNER_STORAGE_KEY,
        new Date().toISOString(),
      );
    } catch {
      // localStorage indisponible — le dismissal n'est pas persisté, l'encart reviendra au prochain load.
    }
  }
}
